import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQuery,
  CREATE_TRADE_DATA,
  DELETE_TRADE_DATA,
  GET_ALL_TRADEDATA_LOGS,
  GET_ALL_TRADEDATA_REAL,
  GET_ENTRY_SUMMARIES,
  GET_TARIFF_ANALYSIS_FINDINGS,
  GET_TARIFF_ANALYSIS_AUTHORITY_SUMMARY,
  GET_TARIFF_ANALYSIS_AUTHORITY_HTS_SUMMARY,
  GET_TARIFF_ANALYSIS_AUTHORITY_ENTRIES,
  GET_TARIFF_ANALYSIS_AI_BUCKETS,
  GET_TARIFF_ANALYSIS_AI_ENRICHMENTS,
  TRIGGER_TARIFF_ANALYSIS_AI_ENRICHMENT,
  TRIGGER_TARIFF_ANALYSIS_AI_ENRICHMENT_BULK,
  START_TARIFF_ANALYSIS_BUILD,
  CLEAR_TARIFF_RATE_CACHE,
  GET_TRADE_DATA_COLUMNS,
} from "@/graphql";
interface TradeDataLogsReponse {
  getTasksByTenant: TradeDataLogsTypes;
}
interface PostTradeDataResponse {
  createTradeData: {
    data: string;
  };
}

type TariffAnalysisInput = {
  pageNo?: number;
  pageSize?: number;
  sortBy?: string;
  sortType?: number;
  clientId?: string;
  tenantId?: string;
  searchKeyword?: string;
  filters?: Record<string, any>;
  dateFrom?: string;
  dateTo?: string;
  signs?: string[];
  authorities?: string[];
  hasCh99?: boolean;
  metalSplitOnly?: boolean;
  minAbsDifference?: number;
  maxAbsDifference?: number;
  authority?: string;
  htsNumberFull?: string;
};

export const tradeDataApi = createApi({
  reducerPath: "api/tradeData",
  tagTypes: ["tradeData", "tradeDataLogs"],
  baseQuery,
  endpoints: (builder) => ({
    getAllTradeData: builder.query<
      TradeDataResponse,
      {
        pageNo: number;
        pageSize: number;
        sortBy?: string;
        sortType?: number;
        clientId?: string;
        searchKeyword?: string;
        tenantId?: string;
        filters?: Record<string, any>;
        dateFrom?: string;
        dateTo?: string;
      }
    >({
      query: (variables) => ({
        document: GET_ALL_TRADEDATA_REAL,
        variables: { ...variables },
      }),
      providesTags: ["tradeData"],
      transformResponse: (response: TradeDataResponse) => response,
    }),
    getEntrySummaries: builder.query<
      {
        getEntrySummaries: {
          data: any[];
          meta: { countDocs: number; pageNo: number; pageSize: number };
          filterOptions: {
            availablePorts: string[];
            availableCountries: string[];
            availableManufacturers: string[];
            availableFilerCodes: string[];
            availableLiquidationStatuses: string[];
          };
        };
      },
      {
        pageNo: number;
        pageSize: number;
        sortBy?: string;
        sortType?: number;
        clientId?: string;
        tenantId?: string;
        dateFrom?: string;
        dateTo?: string;
        filters?: Record<string, any>;
      }
    >({
      query: (variables) => ({
        document: GET_ENTRY_SUMMARIES,
        variables: { ...variables },
      }),
      providesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    getTradeDataColumns: builder.query<
      { data: string[] },
      {
        clientId?: string;
        tenantId?: string;
      }
    >({
      query: (variables) => ({
        document: GET_TRADE_DATA_COLUMNS,
        variables: { ...variables },
      }),
      transformResponse: (response: { getTradeDataColumns: { data: string[] } }) => 
        response.getTradeDataColumns,
    }),
    getTariffAnalysisFindings: builder.query<
      { tariffAnalysisFindings: { data: any[]; meta: { countDocs: number; pageNo: number; pageSize: number } } },
      {
        input: TariffAnalysisInput;
      }
    >({
      query: (variables) => ({
        document: GET_TARIFF_ANALYSIS_FINDINGS,
        variables,
      }),
      providesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    getTariffAnalysisAuthoritySummary: builder.query<
      { tariffAnalysisAuthoritySummary: { data: any[]; build?: any } },
      { input: TariffAnalysisInput }
    >({
      query: (variables) => ({
        document: GET_TARIFF_ANALYSIS_AUTHORITY_SUMMARY,
        variables,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    getTariffAnalysisAuthorityHtsSummary: builder.query<
      { tariffAnalysisAuthorityHtsSummary: { data: any[] } },
      { input: TariffAnalysisInput }
    >({
      query: (variables) => ({
        document: GET_TARIFF_ANALYSIS_AUTHORITY_HTS_SUMMARY,
        variables,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    getTariffAnalysisAuthorityEntries: builder.query<
      { tariffAnalysisAuthorityEntries: { data: any[]; meta: { countDocs: number; pageNo: number; pageSize: number } } },
      { input: TariffAnalysisInput }
    >({
      query: (variables) => ({
        document: GET_TARIFF_ANALYSIS_AUTHORITY_ENTRIES,
        variables,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    getTariffAnalysisAiBuckets: builder.query<
      {
        tariffAnalysisAiBuckets: {
          data?: {
            clientId: string
            findingsCount: number
            refundFindingsCount: number
            complianceFindingsCount: number
            absDollars: number
            buckets: Array<{
              name: string
              action: string
              tier: number
              patternId: string
              direction: "refund" | "compliance"
              subKey?: string | null
              absDollars: number
              pctOfClient: number
              netDollars: number
              findingsCount: number
              // Full TariffAnalysisFinding shape — mirrors the flat-table row.
              findings: any[]
              sampleHts: string[]
              sampleCh99: string[]
              sampleCoo: string[]
            }>
          } | null
          build?: any
        }
      },
      { input: TariffAnalysisInput }
    >({
      query: (variables) => ({
        document: GET_TARIFF_ANALYSIS_AI_BUCKETS,
        variables,
      }),
      keepUnusedDataFor: 0,
      providesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    getTariffAnalysisAiEnrichments: builder.query<
      {
        tariffAnalysisAiEnrichments: {
          data: Array<{
            bucketKey: string
            patternId: string
            direction: 'refund' | 'compliance'
            status: 'queued' | 'running' | 'done' | 'partial' | 'failed'
            modelVersion?: string
            iterations?: number
            lastEnrichedAt?: string
            computedAt?: string
            startedAt?: string
            lastHeartbeatAt?: string
            currentStage?: 'sampling' | 'researching' | 'synthesizing' | 'verifying'
            currentTool?: string
            title?: string
            summary?: string
            recommendedAction?: string
            confidence?: 'high' | 'medium' | 'low'
            verdict?: 'confirmed' | 'systemError' | 'ambiguous'
            verdictReason?: string
            citations?: Array<{
              sourceType: string
              identifier: string
              url: string
              excerpt?: string
              relevance?: string
              verified: boolean
            }>
            sampledFindingIds?: string[]
            error?: string
          }>
        }
      },
      { input: TariffAnalysisInput }
    >({
      query: (variables) => ({
        document: GET_TARIFF_ANALYSIS_AI_ENRICHMENTS,
        variables,
      }),
      keepUnusedDataFor: 30,
      providesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    triggerTariffAnalysisAiEnrichment: builder.mutation<
      { triggerTariffAnalysisAiEnrichment: { bucketKey: string; status: string; iterations: number } },
      { input: { tenantId?: string; clientId: string; bucketKey: string; force?: boolean } }
    >({
      query: (variables) => ({
        document: TRIGGER_TARIFF_ANALYSIS_AI_ENRICHMENT,
        variables,
      }),
      invalidatesTags: ["tradeData"],
    }),
    triggerTariffAnalysisAiEnrichmentBulk: builder.mutation<
      { triggerTariffAnalysisAiEnrichmentBulk: { clientId: string; queued: number } },
      { input: { tenantId?: string; clientId: string; force?: boolean } }
    >({
      query: (variables) => ({
        document: TRIGGER_TARIFF_ANALYSIS_AI_ENRICHMENT_BULK,
        variables,
      }),
      invalidatesTags: ["tradeData"],
    }),
    startTariffAnalysisBuild: builder.mutation<
      { startTariffAnalysisBuild: { taskId: string; uploadTime: string; progressBar: boolean } },
      {
        input: {
          pageNo?: number;
          pageSize?: number;
          sortBy?: string;
          sortType?: number;
          clientId?: string;
          tenantId?: string;
          searchKeyword?: string;
          filters?: Record<string, any>;
          dateFrom?: string;
          dateTo?: string;
        };
      }
    >({
      query: (variables) => ({
        document: START_TARIFF_ANALYSIS_BUILD,
        variables,
      }),
      invalidatesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    clearTariffRateCache: builder.mutation<
      { clearTariffRateCache: boolean },
      {
        tenantId?: string;
        clientId?: string;
        triggerRebuild?: boolean;
      }
    >({
      query: (variables) => ({
        document: CLEAR_TARIFF_RATE_CACHE,
        variables,
      }),
      invalidatesTags: ["tradeData"],
      transformResponse: (response: any) => response,
    }),
    postTradeData: builder.mutation<
      PostTradeDataResponse["createTradeData"],
      CreateTradeDataDTO
    >({
      query: ({ tradeData }) => ({
        document: CREATE_TRADE_DATA,
        variables: { tradeData },
      }),
      invalidatesTags: ["tradeData"],
      transformResponse: (response: PostTradeDataResponse) =>
        response.createTradeData,
    }),
    getAllTradeDataLogs: builder.query<
      TradeDataLogsReponse["getTasksByTenant"],
      {
        input: {
          clientId: string;
          uploadTime: string;
        };
      }
    >({
      query: (variables) => {
        return {
          document: GET_ALL_TRADEDATA_LOGS,
          variables: { ...variables },
        };
      },
      providesTags: ["tradeDataLogs"],
      transformResponse: (response: TradeDataLogsReponse) => {
        return response.getTasksByTenant;
      },
    }),
    deleteTradeData: builder.mutation<
      DeleteTradeDataResponse["deleteAllTradeData"],
      DeleteTradeDataVariables
    >({
      query: ({ tenantId, clientId }) => ({
        document: DELETE_TRADE_DATA,
        variables: { tenantId, clientId },
      }),
      invalidatesTags: ["tradeData"],
      transformResponse: (response: DeleteTradeDataResponse) => response.deleteAllTradeData,
    }),
  }),
});

export const {
  useGetAllTradeDataQuery,
  useLazyGetAllTradeDataQuery,
  useGetEntrySummariesQuery,
  useGetTradeDataColumnsQuery,
  useGetTariffAnalysisFindingsQuery,
  useLazyGetTariffAnalysisFindingsQuery,
  useGetTariffAnalysisAuthoritySummaryQuery,
  useGetTariffAnalysisAuthorityHtsSummaryQuery,
  useGetTariffAnalysisAuthorityEntriesQuery,
  useGetTariffAnalysisAiBucketsQuery,
  useGetTariffAnalysisAiEnrichmentsQuery,
  useTriggerTariffAnalysisAiEnrichmentMutation,
  useTriggerTariffAnalysisAiEnrichmentBulkMutation,
  useStartTariffAnalysisBuildMutation,
  useClearTariffRateCacheMutation,
  usePostTradeDataMutation,
  useDeleteTradeDataMutation,
  // Logs api
  useGetAllTradeDataLogsQuery,
  useLazyGetAllTradeDataLogsQuery,
} = tradeDataApi;
